import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ClientEvent } from "@gwrctl/shared";
import { client, hasSession } from "./client.js";

type Listener = (event: ClientEvent) => void;

interface EventsValue {
  /** Belirli olay tiplerini dinle; temizleyici döner. */
  subscribe: (listener: Listener) => () => void;
  connected: boolean;
}

const EventsContext = createContext<EventsValue | null>(null);

/**
 * Tek bir WebSocket bağlantısı tüm uygulama için paylaşılır. Her panel kendi
 * bağlantısını açsaydı, canlı log akan bir sayfada onlarca soket olurdu.
 */
export function EventsProvider({ children }: { children: ReactNode }): ReactNode {
  const listeners = useRef(new Set<Listener>());
  const [connected, setConnected] = useState(false);
  const queryClient = useQueryClient();
  const wasConnected = useRef(false);

  // Kopukken kaçırılan job.update olayları geri gelmez: yeniden bağlanınca
  // ekrandaki tüm veriyi tazele, yoksa biten iş "çalışıyor" kalır.
  useEffect(() => {
    if (connected && wasConnected.current === false) {
      void queryClient.invalidateQueries();
    }
    wasConnected.current = connected;
  }, [connected, queryClient]);

  useEffect(() => {
    // Oturum yokken bağlanmaya çalışmak anlamsız: sunucu 4401 ile kapatır ve istemci
    // sonsuz yeniden bağlanma döngüsüne girer (giriş ekranında konsolu hata dolar).
    if (!hasSession()) return;

    const disconnect = client.connectEvents(
      (event) => {
        for (const listener of listeners.current) listener(event);
      },
      setConnected,
    );
    return disconnect;
  }, []);

  const value = useMemo<EventsValue>(
    () => ({
      connected,
      subscribe: (listener) => {
        listeners.current.add(listener);
        return () => listeners.current.delete(listener);
      },
    }),
    [connected],
  );

  return <EventsContext.Provider value={value}>{children}</EventsContext.Provider>;
}

export function useEvents(): EventsValue {
  const ctx = useContext(EventsContext);
  if (!ctx) throw new Error("EventsProvider eksik");
  return ctx;
}

/** Bir olay tipini dinlemenin kısa yolu. */
export function useEventListener(
  handler: Listener,
  deps: React.DependencyList = [],
): void {
  const { subscribe } = useEvents();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => subscribe((e) => ref.current(e)), [subscribe, ...deps]);
}
